import { type LinkProps, Link as MUILink } from '@mui/material';
import { memo, type PropsWithChildren } from 'react';
import { TOC_LINK_VARIANTS, type TocLinkVariant } from '../types';
import { SectionName } from './SectionName';

type Props = PropsWithChildren<
  Omit<LinkProps, 'variant'> & {
    handleRef?: (ref: HTMLAnchorElement | null, headingId: string) => void;
    headingId: string;
    isSelected: boolean;
    variant?: TocLinkVariant;
  }
>;

const defaultSx = {
  display: 'flex',
  color: 'black',
  paddingY: 2,
  paddingLeft: 3,
  paddingRight: 2,
  borderLeft: '4px solid',
  borderLeftColor: 'transparent',
  '&:active': {
    outline: '2px solid',
    outlineOffset: '-1px',
    outlineColor: 'secondary.main',
    outlineStyle: 'auto',
  },
  '&.selected': {
    backgroundColor: 'blues.lightest',
    borderLeftColor: 'primary.main',
  },
  '&:hover': {
    textDecoration: 'none',
    backgroundColor: 'neutral.lightest',
    borderLeftColor: 'primary.main',
  },
  '&:focus-visible': {
    backgroundColor: 'blues.lightest',
    borderLeftColor: 'primary.main',
  },
};

const linearProgressVariantSx = {
  display: 'flex',
  borderLeftColor: 'transparent',
  '&:hover': {
    textDecoration: 'underline',
  },
  '&:focus-visible': {
    textDecoration: 'underline',
    color: 'nwGreens.light',
    marginLeft: 1,
  },
};

export const Link = memo(function Link({
  handleRef,
  headingId,
  isSelected,
  variant = TOC_LINK_VARIANTS.DEFAULT,
  children,
  ...rest
}: Props) {
  const isDefaultVariant = variant === TOC_LINK_VARIANTS.DEFAULT;
  const isLinearProgressVariant = variant === TOC_LINK_VARIANTS.LINEAR_PROGRESS;
  return (
    <MUILink
      key={headingId}
      href={`#${headingId}`}
      underline="none"
      {...(handleRef && {
        ref: (ref: HTMLAnchorElement | null) => handleRef(ref, headingId),
      })}
      sx={{
        ...(isDefaultVariant ? defaultSx : linearProgressVariantSx),
        ...(isLinearProgressVariant && {
          color: isSelected ? 'primary.main' : 'neutral.dark',
          textDecoration: isSelected ? 'underline' : 'none',
        }),
      }}
      {...rest}
    >
      <SectionName bold={isSelected} variant={variant}>
        {children}
      </SectionName>
    </MUILink>
  );
});
